import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { SpinnerComponent } from './NewComponents';
import RCSTemplateCard from './RCSTemplatesCard';
import { getRCSTemplatesALL } from '../../../Services';

interface RCSTemplate {
    _id: string;
    id: string;
    templateName: string;
    description?: string;
    updatedAt?: string | Date;
    previewImage?: string;
    status: string;
}

interface RcsTemplatesProps {
    onclickAction: (template: RCSTemplate) => void;
}

const RcsTemplates: React.FC<RcsTemplatesProps> = ({ onclickAction }) => {
    const authtoken = useSelector((state: any) => state.main.auth_token);
    const localdata = useSelector((state: any) => state.main.localdata);

    const [isLoadingTemplates, setIsLoadingTemplates] = useState<boolean>(false);
    const [fullTemplateData, setFullTemplateData] = useState<any>(null);
    const [templateData, setTemplateData] = useState<RCSTemplate[]>([]);

    // pagination states
    const [page, setPage] = useState<number>(1);
    const [limit] = useState<number>(10);
    const [hasMore, setHasMore] = useState<boolean>(true);

    useEffect(() => {
        fetchRcsTemplates(page, limit);
    }, [page]);

    const fetchRcsTemplates = async (pageNum: number, pageLimit: number) => {
        try {
            setIsLoadingTemplates(true);

            const response = await getRCSTemplatesALL(authtoken, {
                senderId: localdata?.rcsUserDetails?.senderId,
                page: pageNum,
                limit: pageLimit,
                status: 'approved',
            });

            const rawTemplates = response?.data?.templateInfo || [];

            const mappedTemplates: RCSTemplate[] = rawTemplates
                .map((item: any) => {
                    const t = item.viTemplate;
                    return {
                        id: item._id,
                        templateName: t.name,
                        description:
                            t.type === 'text'
                                ? t.textMessage
                                : t.standaloneCard?.cardDescription ||
                                t.carouselCard?.[0]?.cardDescription ||
                                'No description',
                        updatedAt: t.lastUpdate,
                        previewImage:
                            t.standaloneCard?.mediaUrl && !t.standaloneCard?.mediaUrl.includes("mp4") ? t.standaloneCard?.thumbnailUrl : t.carouselCard?.[0]?.mediaUrl && !t.carouselCard?.[0]?.mediaUrl.includes("mp4") ? t.carouselCard?.[0]?.thumbnailUrl : null,
                        status: t.status?.toLowerCase(),
                    };
                })
                .filter((t: RCSTemplate) => t.status === 'approved');

            if (mappedTemplates.length < pageLimit) {
                setHasMore(false); // no more templates left
            }

            // append data for infinite scroll style, or replace for page-by-page style
            setFullTemplateData((prev: any) => (pageNum === 1 ? rawTemplates : [...prev, ...rawTemplates]));
            setTemplateData((prev: RCSTemplate[]) =>
                pageNum === 1 ? mappedTemplates : [...prev, ...mappedTemplates]
            );
        } catch (error) {
            console.error('Error fetching RCS templates:', error);
        } finally {
            setIsLoadingTemplates(false);
        }
    };

    return (
        <div
            className="w-100 verythinscrollnew"
            style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
        >
            <div className="w-100 verythinscrollnew" style={{ flex: 1, overflowY: 'auto' }}>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(280px, 1fr))',
                        gap: '16px',
                        padding: '16px',
                        marginBottom: '16px',
                    }}
                >
                    {isLoadingTemplates && page === 1 ? (
                        <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px 0' }}>
                            <SpinnerComponent />
                        </div>
                    ) : templateData?.length > 0 ? (
                        <>
                            {templateData.map((template) => (
                                <RCSTemplateCard
                                    key={template.id}
                                    template={template}
                                    onClick={() => {
                                        onclickAction({
                                            id: template.id,
                                            ...(fullTemplateData.find((t: any) => t._id === template.id) || {})
                                                .viTemplate,
                                        });
                                    }}
                                />
                            ))}
                            {/* Pagination button */}
                            {hasMore && (
                                <div style={{ gridColumn: '1 / -1', textAlign: 'center', marginTop: '16px' }}>
                                    <button
                                        onClick={() => setPage((prev) => prev + 1)}
                                        style={{
                                            padding: '8px 16px',
                                            background: '#2563EB',
                                            color: '#fff',
                                            borderRadius: 6,
                                            border: 'none',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {isLoadingTemplates ? 'Loading...' : 'Load More'}
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px 0' }}>
                            <p style={{ color: '#6B7280', fontSize: '16px' }}>No approved templates found</p>
                            <p
                                style={{
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    marginBottom: '8px',
                                    color: '#333',
                                }}
                            >
                                You have not created any approved RCS templates yet.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RcsTemplates;
